import { useMemo, useState } from "react";
import { Minus, Plus, RefreshCw } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger, cn } from "@ncp/ui";
import { parsePatch, prettyJson, type ChangeSetItem, type JsonPatchOp } from "./api";

function OpBadge({ op }: { op: JsonPatchOp["op"] }) {
  const cls = op === "add" ? "bg-success-50 text-success-700" : op === "remove" ? "bg-danger-50 text-danger-700" : "bg-warning-50 text-warning-700";
  const Icon = op === "add" ? Plus : op === "remove" ? Minus : RefreshCw;
  return <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px] uppercase", cls)}><Icon className="size-3" /> {op}</span>;
}

function short(value: unknown, max = 80): string {
  const s = typeof value === "string" ? value : JSON.stringify(value);
  if (s === undefined) return "";
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** Side-by-side / patch view of one change-set item: the RFC 6902 patch computed at add time plus the raw before/after payloads. */
export function DiffViewer({ item }: { item: ChangeSetItem }) {
  const { t } = useTranslation();
  const ops = useMemo(() => parsePatch(item.diffJson), [item.diffJson]);
  const [tab, setTab] = useState<"patch" | "before" | "after">("patch");
  const grouped = useMemo(() => {
    const g = new Map<string, JsonPatchOp[]>();
    for (const op of ops) {
      const root = op.path.split("/")[1] ?? "";
      const list = g.get(root) ?? [];
      list.push(op);
      g.set(root, list);
    }
    return [...g.entries()];
  }, [ops]);

  return (
    <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
      <TabsList className="mb-3">
        <TabsTrigger value="patch">{t("admin:releases.diff.patch")} <Badge variant="outline" size="sm" className="ms-1">{ops.length}</Badge></TabsTrigger>
        <TabsTrigger value="before">{t("admin:releases.diff.before")}</TabsTrigger>
        <TabsTrigger value="after">{t("admin:releases.diff.after")}</TabsTrigger>
      </TabsList>
      <TabsContent value="patch">
        {ops.length === 0 ? (
          <p className="rounded-xl bg-sand-50 p-4 text-sm text-muted-foreground">{item.previousVersion == null && item.oldValueJson == null ? t("admin:releases.diff.firstVersion") : t("admin:releases.diff.noChanges")}</p>
        ) : (
          <div className="space-y-3">
            {grouped.map(([root, list]) => (
              <div key={root} className="rounded-xl border border-sand-200">
                <div className="flex items-center justify-between border-b border-sand-100 bg-sand-50 px-3 py-1.5"><span className="font-mono text-xs font-semibold">/{root}</span><Badge variant="outline" size="sm">{list.length}</Badge></div>
                <ul className="divide-y divide-sand-100 text-xs" dir="ltr">
                  {list.map((op, i) => (
                    <li key={i} className="grid grid-cols-[auto_minmax(0,1fr)] gap-3 px-3 py-1.5">
                      <OpBadge op={op.op} />
                      <div className="min-w-0">
                        <p className="truncate font-mono text-[11px] text-sand-700">{op.path}</p>
                        {op.op !== "remove" ? <p className={cn("mt-0.5 break-all font-mono text-[11px]", op.op === "add" ? "text-success-700" : "text-warning-800")}>{short(op.value, 240)}</p> : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </TabsContent>
      <TabsContent value="before"><pre className="max-h-[480px] overflow-auto rounded-xl bg-sand-50 p-3 font-mono text-[11px] leading-relaxed" dir="ltr">{prettyJson(item.oldValueJson) || t("admin:releases.diff.nothing")}</pre></TabsContent>
      <TabsContent value="after"><pre className="max-h-[480px] overflow-auto rounded-xl bg-sand-50 p-3 font-mono text-[11px] leading-relaxed" dir="ltr">{prettyJson(item.newValueJson) || t("admin:releases.diff.nothing")}</pre></TabsContent>
    </Tabs>
  );
}
